import { useCallback } from 'react';

import type { InteractionTrackingEvent } from '@xing-com/crate-communication-tracking';
import { useTracking } from '@xing-com/crate-communication-tracking';
import type { ApplicationType } from '@xing-com/crate-jobs-helpers';
import type { PropTrackActionData } from '@xing-com/crate-jobs-hooks';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { unscrambleId } from '@xing-com/crate-utils-x1-data';

import { applicationTypeNwtContext } from '../../../tracking';

type Props = { jobId: string; applicationType?: ApplicationType };
type CallbackProps = { action: 'start' | 'exit' | 'interview' };
type BookInterviewButtonTrackingCallback = (props: CallbackProps) => void;
type AdobeTrackingProps = Required<PropTrackActionData> & {
  PropJobsId: number | string;
};

export const useBookInterviewButtonTracking = ({
  jobId,
  applicationType,
}: Props): BookInterviewButtonTrackingCallback => {
  const nwtTrack = useJobsNwtTracking();
  const { track: trackAdobeEvent } =
    useTracking<InteractionTrackingEvent<AdobeTrackingProps>>();

  return useCallback(
    ({ action }) => {
      const nwtDefaultProps = {
        eventSchema: 'basic',
        page: 'jobs/detail',
        itemUrn: `surn:x-xing:jobs:posting:${jobId}`,
      } as const;

      trackAdobeEvent({
        type: 'interaction',
        event: 'PropTrackAction',
        PropTrackAction: `jobs_apply_${action}`,
        PropActionOrigin: 'job_interview_instant_apply',
        PropJobsId: unscrambleId(jobId),
      });
      switch (action) {
        case 'start':
          nwtTrack({
            ...nwtDefaultProps,
            event: 'intended',
            intention: 'apply',
            element: 'interview_button',
            context: applicationType
              ? applicationTypeNwtContext[applicationType]
              : '',
          });
          break;
        case 'interview':
          nwtTrack({
            ...nwtDefaultProps,
            event: 'visited',
            element: 'book_interview',
            elementDetail: 'interview_instant_apply',
          });
          break;
        case 'exit':
          nwtTrack({
            ...nwtDefaultProps,
            event: 'clicked',
            element: 'exit',
            elementDetail: 'application_in_progress',
            context: 'interview_instant_apply',
          });
          break;
      }
    },
    [applicationType, jobId, nwtTrack, trackAdobeEvent]
  );
};
